/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputCriteriaDateConfiguration} from './configurations/input-criteria-date-configuration';
import {StatefulComponent} from '../../../stateful/stateful.component';
import {InputCriteriaDateState} from './models/input-criteria-date.state';
import {SelectDropdownOptionModel} from '../shared/select-dropdown/models/select-dropdown-option.model';
import {InputCriteriaDateCodeEnum} from './configurations/input-criteria-date-code.enum';
import {AbstractControl} from '@angular/forms';
import {InputMessagesService} from '../../../input/service/input-messages.service';
import {WarningsEnum} from '../../../warnings/warnings.enum';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';
import {GlobalSearchCriteriaService} from '../../services/global-search-criteria.service';
import {InputCriteriaTypeMapper} from '../mappers/input-criteria-type.mapper';
import {GlobalSearchCriterionFilterModel} from '../../models/global-search-criterion-filter.model';
import {DatepickerHelperService} from '../../../utils/datepicker-helper/datepicker-helper.service';
import {DateCriterionModel} from '../../../../bundles/models/date-criterion/date-criterion.model';
import {BundlesConfigService} from '../../../../bundles/services/bundles-config.service';

@Component({
    selector: 'app-input-criteria-date',
    templateUrl: './input-criteria-date.component.html',
    styleUrls: ['./input-criteria-date.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputCriteriaDateComponent extends StatefulComponent<InputCriteriaDateState> implements OnInit, OnDestroy {
    @Input() searchCriteriaDateChange: EventEmitter<void>;
    @Output() searchCriteriaDateReset = new EventEmitter<void>();

    inputCriteriaDateConfiguration = InputCriteriaDateConfiguration;
    inputCriteriaDateCode = InputCriteriaDateCodeEnum;

    private requestedDateInputMessages = new Map<WarningsEnum, string[]>();
    private subscriptions = new Subscription();

    constructor(private inputMessagesService: InputMessagesService,
                private globalSearchService: GlobalSearchCriteriaService,
                private bundlesConfigService: BundlesConfigService,
                private datepickerHelperService: DatepickerHelperService) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(this.searchCriteriaDateChange?.subscribe(() => {
            this.clearTypedDate();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    selectedOptionHandler(option: SelectDropdownOptionModel): void {
        this.storeState({
            selectedCriteriaDate: option,
            startDate: null,
            endDate: null
        });
    }

    setStartDateMaxDate(): NgbDate {
        let date: NgbDate = null;
        const endDate = this.state.endDate;
        if (endDate && this.isSelectedOptionIntervalDate()) {
            // Should disable endDate day and previous days
            date = new NgbDate(endDate.year, endDate.month, endDate.day - 1);
        }
        return date;
    }

    setEndDateMinDate(): NgbDate {
        let date: NgbDate = null;
        const startDate = this.state.startDate;
        if (startDate && this.isSelectedOptionIntervalDate()) {
            // Should disable start day and following days
            date = new NgbDate(startDate.year, startDate.month, startDate.day + 1);
        }
        return date;
    }

    inputStartDateHandler(date: NgbDate): void {
        this.storeState({
            startDate: date
        });

        if (date && this.requestedDateInputMessages.get(WarningsEnum.ERROR)?.length === 0) {
            this.submitCriteriaStartDateHandler(date);
        }
    }

    inputEndDateHandler(date: NgbDate): void {
        this.storeState({
            endDate: date
        });

        if (date && this.requestedDateInputMessages.get(WarningsEnum.ERROR)?.length === 0) {
            this.submitCriteriaEndDateHandler(date);
        }
    }

    manageRequestedDateInputStateChange(date: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(date,
                this.requestedDateInputMessages, 'error');
        this.requestedDateInputMessages = new Map(this.requestedDateInputMessages);
    }

    private isSelectedOptionIntervalDate(): boolean {
        return this.state?.selectedCriteriaDate?.code === InputCriteriaDateCodeEnum.BETWEEN_INTERVAL;
    }

    private submitCriteriaStartDateHandler(startDate: NgbDate): void {
        if (this.isSelectedOptionIntervalDate()) {
            const endDate = this.state.endDate;
            if (endDate) {
                this.submitCriteriaDate(startDate, endDate);
            }
        } else {
            this.submitCriteriaDate(startDate, null);
        }
    }

    private submitCriteriaEndDateHandler(endDate: NgbDate): void {
        if (this.isSelectedOptionIntervalDate()) {
            const startDate = this.state.startDate;
            if (startDate) {
                this.submitCriteriaDate(startDate, endDate);
            }
        } else {
            this.submitCriteriaDate(null, endDate);
        }
    }

    private submitCriteriaDate(startDate: NgbDate, endDate: NgbDate): void {

        const criterionFilterWithDateSuffix: GlobalSearchCriterionFilterModel = {
            ...this.globalSearchService.getSelectedSearchCriterionFilter(),
            criterionSuffix: this.state.selectedCriteriaDate.resultPrefix.toLowerCase()
        };

        const date: DateCriterionModel = {
            startDate: this.datepickerHelperService.convertNgbDateToTimestampTimeZoneStartDate(
                    startDate, this.bundlesConfigService.timezone),
            endDate: this.datepickerHelperService.convertNgbDateToTimestampTimeZoneEndDate(
                    endDate, this.bundlesConfigService.timezone)
        };
        const queryFilter = InputCriteriaTypeMapper.fromInputCriteriaTypeToQueryChipsAreaModel(
                criterionFilterWithDateSuffix,
                this.getQueryFilterValue(startDate, endDate),
                date);

        if (queryFilter) {
            this.globalSearchService.addCriterionFilterToQuery(queryFilter);

            this.clearTypedDate();
            this.searchCriteriaDateReset.emit();
        }
    }

    private getQueryFilterValue(startDate: NgbDate, endDate: NgbDate): string {
        let formattedValue = '';
        if (startDate && endDate) {
            formattedValue = `${this.getFormattedDateString(startDate)} to ${this.getFormattedDateString(endDate)}`;
        } else if (startDate) {
            formattedValue = this.getFormattedDateString(startDate);
        } else if (endDate) {
            formattedValue = this.getFormattedDateString(endDate);
        }

        return formattedValue;
    }

    private getFormattedDateString(date: NgbDate): string {
        if (date) {
            return `${date.day}-${date.month}-${date.year}`;
        }
    }

    private clearTypedDate(): void {
        this.storeState({
            selectedCriteriaDate: null,
            startDate: null,
            endDate: null
        });
    }
}
